import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useState } from "react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte.
import { formatDate } from "../../lib/format";
import { RefreshCw, XCircle, Loader2, Undo2, Paperclip, CheckCircle2 } from "lucide-react";
import { NOT_RENEWED_REASONS } from "./index";

/**
 * RenewalPolicyDialog - Dialog for managing policy renewals
 */
export function RenewalPolicyDialog({
  open,
  onOpenChange,
  policy,
  getClientName,
  renewalData,
  setRenewalData,
  onRenew,
  onUndo,
  undoing,
  saving,
  renewalResult = null,
  uploading = false,
  onAttachDocument = null,
  onSkipAttach = null,
}) {
  const [attachFile, setAttachFile] = useState(null);
  if (!policy) return null;

  // Frequenza del nuovo contratto: se Gianni non sceglie, si tiene quella attuale
  const frequenzaScelta = renewalData.newPaymentFrequency
    || (policy.payment_frequency === "semiannual" ? "semestral" : "annual");

  // Data proposta per la prima rata: 6 mesi dopo la decorrenza (che è la scadenza attuale)
  const rataProposta = (() => {
    const base = policy.expiry_date;
    if (!base) return "";
    const d = new Date(base + "T00:00:00");
    if (isNaN(d.getTime())) return "";
    d.setMonth(d.getMonth() + 6);
    return d.toISOString().slice(0, 10);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-500" />
            Gestione Rinnovo Polizza
          </DialogTitle>
          <DialogDescription>
            Seleziona il tipo di rinnovo per questa polizza
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-medium">{getClientName(policy.client_id)}</p>
            <p className="text-sm text-muted-foreground">
              {policy.company} • {policy.policy_number}
            </p>
            {policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
              <p className="text-xs text-orange-600 mt-1">
                Pagamento semestrale • Scadenza rata: {formatDate(policy.semiannual_expiry, "-")}
                {policy.semiannual_paid && " (Pagata)"}
              </p>
            )}
          </div>

          {/* Renewal Type Selection */}
          <div className="space-y-3">
            <Label>Tipo di Operazione</Label>
            <div className="space-y-2">
              {/* Semester payment option - only for semiannual policies with unpaid installment */}
              {policy.payment_frequency === "semiannual" && !policy.semiannual_paid && (
                <div className="flex items-center space-x-2 p-2 bg-orange-50 dark:bg-orange-950 rounded border border-orange-200 dark:border-orange-800">
                  <input
                    type="radio"
                    id="renew_semester"
                    name="renewalType"
                    value="renew_semester"
                    checked={renewalData.renewalType === "renew_semester"}
                    onChange={(e) => setRenewalData(prev => ({ ...prev, renewalType: e.target.value }))}
                    className="w-4 h-4"
                    data-testid="renew-semester-radio"
                  />
                  <label htmlFor="renew_semester" className="text-sm font-medium text-orange-800 dark:text-orange-200">
                    Segna rata semestrale come pagata
                  </label>
                </div>
              )}
              {policy.payment_frequency === "semiannual" && policy.semiannual_paid && (
                <div className="p-2 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800 space-y-2">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Rata semestrale già pagata il {formatDate(policy.semiannual_paid_date, "—")}
                  </p>
                  {onUndo && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onUndo}
                      disabled={undoing || saving}
                      className="w-full border-green-300 text-green-800 hover:bg-green-100 dark:border-green-700 dark:text-green-200 dark:hover:bg-green-900"
                      data-testid="undo-semester-btn"
                    >
                      {undoing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Undo2 className="h-4 w-4 mr-2" />
                      )}
                      Annulla pagamento rata (click sbagliato)
                    </Button>
                  )}
                </div>
              )}
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="same_company"
                  name="renewalType"
                  value="same_company"
                  checked={renewalData.renewalType === "same_company"}
                  onChange={(e) => setRenewalData(prev => ({ ...prev, renewalType: e.target.value }))}
                  className="w-4 h-4"
                  data-testid="renew-same-company-radio"
                />
                <label htmlFor="same_company" className="text-sm">
                  Rinnovo annuale - stessa compagnia
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="other_company"
                  name="renewalType"
                  value="other_company"
                  checked={renewalData.renewalType === "other_company"}
                  onChange={(e) => setRenewalData(prev => ({ ...prev, renewalType: e.target.value }))}
                  className="w-4 h-4"
                  data-testid="renew-other-company-radio"
                />
                <label htmlFor="other_company" className="text-sm">
                  Rinnovo annuale - altra compagnia
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="not_renewed"
                  name="renewalType"
                  value="not_renewed"
                  checked={renewalData.renewalType === "not_renewed"}
                  onChange={(e) => setRenewalData(prev => ({ ...prev, renewalType: e.target.value }))}
                  className="w-4 h-4"
                  data-testid="not-renewed-radio"
                />
                <label htmlFor="not_renewed" className="text-sm">
                  Non rinnovata
                </label>
              </div>
            </div>
          </div>

          {/* New Company Field */}
          {renewalData.renewalType === "other_company" && (
            <div className="space-y-2">
              <Label htmlFor="newCompany">Nuova Compagnia *</Label>
              <Input
                id="newCompany"
                value={renewalData.newCompany}
                onChange={(e) => setRenewalData(prev => ({ ...prev, newCompany: e.target.value.toUpperCase() }))}
                placeholder="Nome della nuova compagnia"
                className="uppercase"
                data-testid="new-company-input"
              />
            </div>
          )}

          {/* Frequenza di pagamento del NUOVO contratto (17/09/2026) */}
          {(renewalData.renewalType === "same_company" || renewalData.renewalType === "other_company") && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-2">
                <Label>Frequenza di pagamento del nuovo contratto</Label>
                <Select
                  value={frequenzaScelta}
                  onValueChange={(value) => setRenewalData(prev => ({ ...prev, newPaymentFrequency: value }))}
                >
                  <SelectTrigger data-testid="new-payment-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annuale — una sola scadenza</SelectItem>
                    <SelectItem value="semestral">Semestrale — due rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {frequenzaScelta === "semestral" && (
                <div className="space-y-2">
                  <Label htmlFor="newSemestralExpiry">Data della prima rata</Label>
                  <Input
                    id="newSemestralExpiry"
                    type="date"
                    value={renewalData.newSemiannualExpiry || rataProposta}
                    onChange={(e) => setRenewalData(prev => ({ ...prev, newSemiannualExpiry: e.target.value }))}
                    data-testid="new-semestral-expiry"
                  />
                  <p className="text-xs text-muted-foreground">
                    Proposta: 6 mesi dopo la decorrenza. Puoi cambiarla.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Not Renewed Reason */}
          {renewalData.renewalType === "not_renewed" && (
            <div className="space-y-2">
              <Label>Motivo Non Rinnovo *</Label>
              <Select
                value={renewalData.notRenewedReason}
                onValueChange={(value) => setRenewalData(prev => ({ ...prev, notRenewedReason: value }))}
              >
                <SelectTrigger data-testid="not-renewed-reason-select">
                  <SelectValue placeholder="Seleziona motivo" />
                </SelectTrigger>
                <SelectContent>
                  {NOT_RENEWED_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* New Premium (for renewals) */}
          {(renewalData.renewalType === "same_company" || renewalData.renewalType === "other_company") && (
            <>
              <div className="space-y-2">
                <Label htmlFor="newPremium">Nuovo Premio (€)</Label>
                <Input
                  id="newPremium"
                  type="number"
                  step="0.01"
                  value={renewalData.newPremium}
                  onChange={(e) => setRenewalData(prev => ({ ...prev, newPremium: e.target.value }))}
                  placeholder={policy.premium?.toString() || "0"}
                  data-testid="new-premium-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newConsultation">Nuova Consulenza (€)</Label>
                <Input
                  id="newConsultation"
                  type="number"
                  step="0.01"
                  value={renewalData.newConsultationFee}
                  onChange={(e) => setRenewalData(prev => ({ ...prev, newConsultationFee: e.target.value }))}
                  placeholder={policy.consultation_fee?.toString() || "0"}
                  data-testid="new-consultation-input"
                />
              </div>
            </>
          )}
          {/* B1 — appena rinnovata: allega subito il documento della nuova polizza */}
          {renewalResult && (
            <div className="space-y-3 rounded-md border border-green-300 bg-green-50 dark:bg-green-950/30 p-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Rinnovo fatto: nuova polizza <b>{renewalResult.new_policy_number}</b>
                {renewalResult.company ? ` · ${renewalResult.company}` : ""}
              </p>
              <div className="space-y-2">
                <Label htmlFor="attachPolicy">📎 Allega la nuova polizza (PDF o foto)</Label>
                <input
                  id="attachPolicy"
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground"
                  data-testid="attach-policy-input"
                />
                <p className="text-xs text-muted-foreground">
                  Il documento viene archiviato sulla polizza nuova, nella cartella giusta e su MEGA.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  disabled={!attachFile || uploading}
                  onClick={() => onAttachDocument && onAttachDocument(attachFile)}
                  data-testid="attach-policy-btn"
                >
                  {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}
                  Allega il documento
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => (onSkipAttach ? onSkipAttach() : onOpenChange(false))}
                  data-testid="skip-attach-btn"
                >
                  Lo carico dopo
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {renewalResult ? (
            <Button variant="outline" onClick={() => (onSkipAttach ? onSkipAttach() : onOpenChange(false))}>
              Chiudi
            </Button>
          ) : (
          <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button 
            onClick={onRenew} 
            disabled={saving}
            className={renewalData.renewalType === "not_renewed" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
            data-testid="confirm-renewal-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : renewalData.renewalType === "not_renewed" ? (
              <XCircle className="h-4 w-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {renewalData.renewalType === "not_renewed" ? "Segna Non Rinnovata" : 
             renewalData.renewalType === "renew_semester" ? "Segna Rata Pagata" : "Rinnova"}
          </Button>
          </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RenewalPolicyDialog;
